import { ChartType, defaults, Plugin } from 'chart.js'
import { formatValue } from 'components_new/organisms/Widget/Types/utils'

export type DrawLatestValueLabelDataset = {
  color: string
  main?: boolean
}

export type DrawLatestValueLabelOpts = {
  datasets: DrawLatestValueLabelDataset[]
  decimals?: number
  placement?: 'inside' | 'outside'
  significantFigures?: number
}

const ARROW_W = 6
const PILL_H = 20
const ARROW_H = 8
const PADDING_X = 7
const RADIUS = 4

const getLastNonNullIndex = (data: (number | null)[]) => {
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i] !== null) return i
  }

  return -1
}

const getFont = () => `600 ${defaults.font.size}px ${defaults.font.family}`

const formatValueWrap = (
  value: number,
  decimals?: number,
  significantFigures?: number
) =>
  formatValue(
    value,
    null,
    decimals ?? 1,
    undefined,
    undefined,
    significantFigures
  ) as string

export const drawLatestValueLabel: Plugin<ChartType, DrawLatestValueLabelOpts> =
  {
    id: 'drawLatestValueLabel',
    beforeLayout(chart, _args, opts) {
      const { ctx } = chart
      const { datasets, decimals, placement, significantFigures } = opts

      let right = 0

      if (placement === 'outside' && datasets?.length) {
        ctx.save()
        ctx.font = getFont()

        chart.data.datasets.forEach((ds, di) => {
          const config = datasets[di]

          if (!config?.main) return
          if (chart.getDatasetMeta(di).hidden) return

          const values = ds.data as (number | null)[]
          const lastIndex = getLastNonNullIndex(values)

          if (lastIndex === -1) return

          const value = values[lastIndex] as number
          const pillW =
            ctx.measureText(
              formatValueWrap(value, decimals, significantFigures)
            ).width +
            PADDING_X * 2

          right = Math.max(right, ARROW_W + pillW + 4)
        })

        ctx.restore()
      }

      const layout = chart.options.layout as any

      if (layout) {
        layout.padding = { ...(layout.padding ?? {}), right }
      }
    },
    afterDraw(chart, _args, opts) {
      const { datasets, decimals, placement, significantFigures } = opts

      if (!datasets?.length) return

      const { ctx, chartArea, scales } = chart
      const yScale = scales.y

      if (!yScale) return

      const inside = placement !== 'outside'

      chart.data.datasets.forEach((ds, di) => {
        const config = datasets[di]

        if (!config?.main) return
        if (chart.getDatasetMeta(di).hidden) return

        const values = ds.data as (number | null)[]
        const lastIndex = getLastNonNullIndex(values)

        if (lastIndex === -1) return

        const value = values[lastIndex] as number
        const color = config.color

        const yPixel = yScale.getPixelForValue(value)
        const yClamped = Math.max(
          chartArea.top + PILL_H / 2,
          Math.min(chartArea.bottom - PILL_H / 2, yPixel)
        )

        const rx = chartArea.right

        ctx.save()
        // Reset clip to full canvas so badge can render in the right padding
        // area
        ctx.beginPath()
        ctx.rect(0, 0, chart.width, chart.height)
        ctx.clip()

        ctx.font = getFont()

        const formatted = formatValueWrap(value, decimals, significantFigures)
        const pillW = ctx.measureText(formatted).width + PADDING_X * 2

        const top = yClamped - PILL_H / 2
        const bottom = yClamped + PILL_H / 2

        ctx.beginPath()

        if (inside) {
          // Arrow tip points right (toward chartArea.right), pill extends left
          const arrowBase = rx - ARROW_W
          const pillLeft = arrowBase - pillW

          ctx.moveTo(rx, yPixel) // arrow tip
          ctx.lineTo(arrowBase, yClamped - ARROW_H / 2) // arrow upper base
          ctx.lineTo(arrowBase, top) // top-right of pill
          ctx.lineTo(pillLeft + RADIUS, top)
          ctx.quadraticCurveTo(pillLeft, top, pillLeft, top + RADIUS)
          ctx.lineTo(pillLeft, bottom - RADIUS)
          ctx.quadraticCurveTo(pillLeft, bottom, pillLeft + RADIUS, bottom)
          ctx.lineTo(arrowBase, bottom) // bottom-right of pill
          ctx.lineTo(arrowBase, yClamped + ARROW_H / 2) // arrow lower base

          ctx.closePath()
          ctx.fillStyle = color
          ctx.fill()

          ctx.fillStyle = '#ffffff'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(formatted, pillLeft + pillW / 2, yClamped)
        } else {
          // Arrow tip points left (toward chartArea.right), pill extends right
          const left = rx + ARROW_W
          const right = left + pillW

          ctx.moveTo(rx, yPixel) // arrow tip
          ctx.lineTo(left, yClamped - ARROW_H / 2) // arrow upper base
          ctx.lineTo(left, top) // top-left of pill
          ctx.lineTo(right - RADIUS, top)
          ctx.quadraticCurveTo(right, top, right, top + RADIUS)
          ctx.lineTo(right, bottom - RADIUS)
          ctx.quadraticCurveTo(right, bottom, right - RADIUS, bottom)
          ctx.lineTo(left, bottom) // bottom-left of pill
          ctx.lineTo(left, yClamped + ARROW_H / 2) // arrow lower base

          ctx.closePath()
          ctx.fillStyle = color
          ctx.fill()

          ctx.fillStyle = '#ffffff'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          ctx.fillText(formatted, left + pillW / 2, yClamped)
        }

        ctx.restore()
      })
    }
  }
